import { useState, useRef, useCallback, useEffect } from "react";
import { ArticleExtraData } from "../../../../../../../articlePage/stateInterfaces";
import { getImageUrl } from "config/apiUrls";
import { minutesDiff } from "./newsFlashSpecialDesktopComponent";
import { getTranslation } from "../../../../autoTickerRowWidget/autoTickerRowTranslations";
import { DateDisplay } from "../../../../../../commonComponents/inline/dateDisplay/dateDisplay";
import { isWcmEditor } from "../../../../../../../config/serverConfig";
import { NewsFlashShareMenu } from "../newsFlashShareMenu";
import { SiteVideoInline } from "../../../../../../siteWidgets/videoPlayer/genericInlineVideoPlayer/siteVideoInline";
import { SiteVideoData } from "../../../../../../interfaces";
import { MediaContentTypes } from "../../../../../../../constants/contentTypes";
import { VideoBehaiforData } from "../../../../../../commonDataObjects/interfaces";
import { SpeedMenuButton } from "../../../../../verticalVideoOverlayPlayer/components/SpeedMenuButton";
import { LinkTargetProperties } from "../../../../../../../global/components/linkSettings/interfaces";
import { HyperLinkBuilder } from "../../../../../../commonComponents/inline/hyperLink";

interface NewsFlashSpecialDesktopPopupItemProps {
    article: ArticleExtraData;
    languageCode: string;
    highlightingDuration: number;
    isExpanded: boolean;
    toAllFlashesLink: string | undefined;
    toAllFlashesLinkTarget: LinkTargetProperties | undefined;
    onItemClick: (articleId: string) => void;
    itemRef: (el: HTMLDivElement | null) => void;
}

export const NewsFlashSpecialDesktopPopupItem = (props: NewsFlashSpecialDesktopPopupItemProps) => {
    const { article, languageCode, highlightingDuration, isExpanded, onItemClick, itemRef, toAllFlashesLink, toAllFlashesLinkTarget } = props;
    const { articleId, roof, title, dateUpdatedOnSite, specialData, video, plainText, author, publishedLink, promotionImageDetails } = article;
    const { mediaLinkType, mediaProportion } = specialData;
    const [isVideoStarted, setIsVideoStarted] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const playerApiRef = useRef(null) as any;

    const lang = languageCode ? languageCode.slice(0, 2) : "he";
    const isRecentItem = highlightingDuration != null && minutesDiff(article) < (highlightingDuration || 1);
    const date = isRecentItem ? getTranslation(lang, "Now") : <DateDisplay date={dateUpdatedOnSite} isMinutesAgo={true} isArticle={true} />;
    const isNewItem = highlightingDuration && minutesDiff(article) < highlightingDuration ? "now" : "";
    const isOpen = isExpanded ? "open" : "";
    const posterUrl = getPosterUrl(article, false);
    const enlargedPosterUrl = getPosterUrl(article, true);
    const mediaId = !isWcmEditor() && mediaLinkType === "video" && video && video[0] && video[0].mediaVideoId ? video[0].mediaVideoId : "";
    const isAuthor = author && author.length > 0;
    const toAllFlashesTitle = getTranslation(lang, "ToAllFlashes");
    const shareText = getTranslation(lang, "Share");
    const isVideo = mediaLinkType === "video" && video && video[0];
    const videoData = isVideo ? buildVideoData(article) : null;
    const isPortrait = mediaProportion === "9:16" || mediaProportion === "6:7";
    const mediaCredit = mediaLinkType === "video" ? video && video[0] && video[0].credit ? video[0].credit : undefined : promotionImageDetails && promotionImageDetails.credit ? promotionImageDetails.credit : undefined;

    const handlePlayerLoaded = useCallback((player: any) => {
        playerApiRef.current = player;
    }, []) as (player: any) => void;

    const handlePlayStarted = useCallback(() => {
        setIsVideoStarted(true);
    }, []) as () => void;

    useEffect(() => {
        if (!isExpanded && playerApiRef.current) {
            try {
                playerApiRef.current.pause();
            } catch (e) {
                console.error("Error pausing video", e);
            }
            setIsVideoStarted(false);
        }
        if (isExpanded && playerApiRef.current) {
            try {
                playerApiRef.current.muted = true;
                setIsMuted(true);
                playerApiRef.current.play();
            } catch (e) {
                console.error("Error auto-playing video", e);
            }
        }
    }, [isExpanded]);

    const handleMuteToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        const player = playerApiRef.current;
        if (player) {
            player.muted = !player.muted;
            setIsMuted(player.muted);
        }
    };

    return (
        <div className={`popup-item ${isNewItem} ${isOpen}`} key={articleId} onClick={() => onItemClick(articleId)} ref={itemRef} data_analytics_id={articleId} >
            <div className="popup-item-top">
                {!isExpanded && hasMedia(article) && posterUrl &&
                    <div className="popup-item-image" data_mediaid_analitycs={mediaId}>
                        <img src={posterUrl} alt={title} />

                        {mediaLinkType === "video" && <div className="popup-video-icon"></div>}
                    </div>
                }

                <div className="popup-item-text">
                    {roof && <span className="popup-item-roof">{roof}</span>}

                    <div className="popup-item-title">{title}</div>

                    {!isExpanded && <div className="popup-item-date">{date}</div>}
                </div>
            </div>

            <div className={`popup-item-content ${isExpanded ? "show" : ""}`}>
                {hasMedia(article) && posterUrl &&
                    <div className={`popup-item-enlarged-media ${isPortrait ? "portrait" : ""}`} data_mediaid_analitycs={mediaId} onClick={(e) => e.stopPropagation()}>
                        {isPortrait && <div className="blur-background" style={{ backgroundImage: `url(${enlargedPosterUrl})` }} />}
                        {isVideo && videoData ?
                            <>
                                <SiteVideoInline
                                    data={videoData}
                                    hideVideoAds={true}
                                    applyStickyness={false}
                                    onPlayerLoaded={handlePlayerLoaded}
                                    onPlayStarted={handlePlayStarted}
                                />

                                {isVideoStarted &&
                                    <div className="video-controls-overlay">
                                        <div className={`mute-btn ${isMuted ? "muted" : ""}`} onClick={handleMuteToggle}></div>

                                        <SpeedMenuButton playerApiRef={playerApiRef} />
                                    </div>
                                }
                            </>
                            :
                            <img src={enlargedPosterUrl} alt={title} />
                        }
                    </div>
                }

                <div className="popup-item-body-text">
                    {plainText}

                    {isAuthor && <div className="popup-item-author">{`(${author}${mediaCredit ? `, ${mediaCredit}` : ""})`}</div>}

                    <div className="popup-item-date">{date}</div>
                </div>

                <div className="popup-share-and-more">
                    <NewsFlashShareMenu
                        title={title}
                        isMobileWeb={false}
                        btnTitle={shareText}
                        articleId={articleId}
                        shareUrl={publishedLink}
                        isNew={isNewItem === "now"}
                    />

                    <div onClick={(e) => e.stopPropagation()}>
                        <HyperLinkBuilder className="to-all-flashes-btn" href={toAllFlashesLink ? toAllFlashesLink : "#"} linkTarget={toAllFlashesLinkTarget ? toAllFlashesLinkTarget : {} as LinkTargetProperties}>
                            <div className="btn-title">{toAllFlashesTitle}</div>
                            <div className="btn-arrow"></div>
                        </HyperLinkBuilder>
                    </div>
                </div>
            </div>
        </div>
    );
};

const getPosterUrl = (article: ArticleExtraData, enlarged: boolean = false) => {
    const { promotionImageDetails, video, specialData } = article;
    if (!specialData) return "";
    const { mediaLinkType } = specialData;
    const size = enlarged ? { w: 420, h: 236 } : { w: 140, h: 79 };

    if (mediaLinkType !== "none" && mediaLinkType !== undefined) {
        if (mediaLinkType === "image") {
            return getImageUrl(promotionImageDetails, size);
        } else if (mediaLinkType === "video" && video && video[0]) {
            return video[0].posterSrc;
        }
    }
    return "";
};

const hasMedia = (article: ArticleExtraData) => {
    if (!article.specialData) return false;
    const { mediaLinkType } = article.specialData;
    return mediaLinkType !== "none" && mediaLinkType !== undefined;
};

const buildVideoData = (article: ArticleExtraData): SiteVideoData | null => {
    const { video, title, specialData } = article;
    if (!video || !video[0]) return null;
    const { videoCredit, videoOriginalCaption } = specialData;

    const VideoBehavior: VideoBehaiforData = {
        isAutoPlaying: true,
        isLoopPlaying: false,
        isPlayerBarVisible: true,
        isSoundButtonVisible: true,
        isMuteAvailable: true,
    };

    return {
        ...VideoBehavior,
        mediaType: MediaContentTypes.MEDIA_VIDEO,
        mediaId: video[0].mediaVideoId,
        credit: videoCredit || "",
        url: video[0].main_link,
        downGradeUrl: video[0].low_res_link,
        title: title || videoOriginalCaption || "",
        poster: video[0].posterSrc,
        duration: video[0].duration,
        youtube_id: video[0].youtube_id,
        blockAbroadViews: false,
    } as SiteVideoData;
};
